import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DOUBLE_CLICK_PREVENTION_MS, MIN_TOUCH_DURATION_MS } from '../../constants/ui';
import { triggerHapticFeedback } from '../../utils/haptic';
import Image from '../Image';
import styles from './styles.module.css';

export enum ButtonType {
  primary = 'primary',
  secondary = 'secondary',
}

interface ButtonProps {
  label: string;
  type: ButtonType;
  icon?: string;
  className?: string;
  classNameIcon?: string;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
  // Enhanced props for duplicate prevention
  loading?: boolean;
  loadingLabel?: string;
  hapticFeedback?: boolean;
  preventDoubleClick?: boolean;
  doubleClickWindow?: number;
  disableClickProtection?: boolean;
}

const Button = ({
  label,
  type,
  icon,
  className,
  classNameIcon,
  disabled,
  onClick,
  loading = false,
  loadingLabel,
  hapticFeedback = false,
  preventDoubleClick = false,
  doubleClickWindow = DOUBLE_CLICK_PREVENTION_MS,
  disableClickProtection = false,
}: ButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const lastClickTimeRef = useRef<number>(0);
  const touchStartTimeRef = useRef<number>(0);
  const lastTouchWasGhostTapRef = useRef<boolean>(false);

  const isButtonDisabled = disabled || isLoading || loading;

  // Reset double-click timestamp on external re-enable so legitimate retries are not blocked.
  const isExternallyDisabled = !!(disabled || loading);
  const wasExternallyDisabledRef = useRef<boolean>(isExternallyDisabled);
  useEffect(() => {
    if (wasExternallyDisabledRef.current && !isExternallyDisabled) {
      lastClickTimeRef.current = 0;
    }
    wasExternallyDisabledRef.current = isExternallyDisabled;
  }, [isExternallyDisabled]);

  // Enhanced click handler with double-click prevention
  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || isLoading) return;

      if (!disableClickProtection) {
        // Gate ghost-tap suppression in click (not touchend.preventDefault) to avoid cancelling legitimate fast taps.
        if (lastTouchWasGhostTapRef.current) {
          lastTouchWasGhostTapRef.current = false;
          return;
        }

        // Prevent double-clicks if enabled
        if (preventDoubleClick) {
          const now = Date.now();
          const timeSinceLastClickInner = now - lastClickTimeRef.current;

          if (timeSinceLastClickInner < doubleClickWindow) return;

          lastClickTimeRef.current = now;
        }
      }

      // Trigger haptic feedback immediately
      if (hapticFeedback) triggerHapticFeedback();

      if (!onClick) return;

      try {
        setIsLoading(true);
        await onClick(e);
      } catch (error) {
        console.error('Button click error:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [disabled, isLoading, onClick, preventDoubleClick, doubleClickWindow, hapticFeedback, disableClickProtection]
  );

  // Prevent accidental double-taps on mobile
  const handleTouchStart = useCallback(() => {
    touchStartTimeRef.current = Date.now();
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (disableClickProtection) {
      lastTouchWasGhostTapRef.current = false;
      return;
    }

    const touchDuration = Date.now() - touchStartTimeRef.current;
    const isGhostTap = touchDuration < MIN_TOUCH_DURATION_MS;

    lastTouchWasGhostTapRef.current = isGhostTap;
  }, [disableClickProtection]);

  // Determine display label
  const displayLabel = (isLoading || loading) && loadingLabel ? loadingLabel : label;

  // Determine if in loading state (internal + external)
  const isLoadingState = isLoading || loading;

  return (
    <button
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className={`${styles.button} ${type === ButtonType.primary ? styles.primary : styles.secondary} ${className} ${isButtonDisabled && styles.disabled} ${isLoadingState ? styles.loading : ''}`}
      disabled={isButtonDisabled}
      aria-label={displayLabel}
      data-testid={`button-${type}`}
      data-loading={isLoadingState}
    >
      <span className={styles.label}>{displayLabel}</span>
      {icon && <Image src={icon} className={classNameIcon ?? ''} />}
    </button>
  );
};

export default Button;
